/**
 * @quotech/ui - Universal Command Palette Component
 * 
 * ALL MODULES MUST USE THIS COMMAND PALETTE
 * 
 * Features:
 * - Triggered by Cmd/Ctrl+K
 * - Search across commands
 * - Quick navigation to other hubs
 * - Recent actions
 * - Grouped commands
 */

import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import { 
  Search, 
  ExternalLink,
  ArrowRight,
} from 'lucide-react'
import { cn } from '../lib/utils'
import type { CommandItem, ExternalLink as ExternalLinkType } from './types'

export interface CommandPaletteProps {
  /** Whether the palette is open */
  isOpen: boolean
  /** Callback to close the palette */
  onClose: () => void
  /** Module-specific commands */
  commands?: CommandItem[]
  /** Cross-module navigation links */
  hubLinks?: ExternalLinkType[]
  /** Recent items */
  recentItems?: CommandItem[]
  /** Placeholder text */
  placeholder?: string
  /** Custom className */
  className?: string
}

export function CommandPalette({
  isOpen,
  onClose,
  commands = [],
  hubLinks = [],
  recentItems = [],
  placeholder = 'Search commands...',
  className,
}: CommandPaletteProps) {
  const [search, setSearch] = useState('')
  const navigate = useNavigate()

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  // Reset search when opened
  useEffect(() => {
    if (isOpen) {
      setSearch('')
    }
  }, [isOpen])

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleSelect = useCallback((item: CommandItem) => {
    item.action()
    onClose()
  }, [onClose])

  const handleNavigate = useCallback((href: string) => {
    if (href.startsWith('http')) {
      window.location.href = href
    } else {
      navigate(href)
    }
    onClose()
  }, [navigate, onClose])

  // Group commands by their group property
  const groupedCommands = commands.reduce((acc, cmd) => {
    const group = cmd.group || 'Actions'
    if (!acc[group]) acc[group] = []
    acc[group].push(cmd)
    return acc
  }, {} as Record<string, CommandItem[]>)

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog - positioned at 15% from top for better visibility */}
      <div className={cn(
        'fixed left-1/2 top-[15%] z-50 w-full max-w-lg -translate-x-1/2',
        'animate-in fade-in-0 zoom-in-95 duration-200',
        className
      )}>
        <Command
          className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-2xl overflow-hidden"
          shouldFilter={true}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 border-b border-slate-200 dark:border-slate-700">
            <Search className="w-5 h-5 text-slate-400" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={placeholder}
              className="flex-1 py-4 text-base bg-transparent outline-none placeholder:text-slate-400 text-slate-900 dark:text-slate-100"
            />
            <kbd className="hidden sm:flex items-center gap-0.5 px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-500 dark:text-slate-400">
              ESC
            </kbd>
          </div>

          {/* Results - constrained to viewport height */}
          <Command.List className="max-h-[60vh] overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              No results found.
            </Command.Empty>

            {/* Recent Items */}
            {recentItems.length > 0 && (
              <Command.Group heading="Recent">
                {recentItems.map((item) => (
                  <Command.Item
                    key={item.id}
                    value={`recent-${item.id} ${item.label} ${item.keywords?.join(' ') || ''}`}
                    onSelect={() => handleSelect(item)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 data-[selected=true]:bg-slate-100 dark:data-[selected=true]:bg-slate-700/50"
                  >
                    {item.icon && <item.icon className="w-4 h-4 text-slate-400" />}
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{item.label}</p>
                      {item.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Grouped Commands */}
            {Object.entries(groupedCommands).map(([group, items]) => (
              <Command.Group key={group} heading={group}>
                {items.map((item) => (
                  <Command.Item
                    key={item.id}
                    value={`${item.id} ${item.label} ${item.keywords?.join(' ') || ''}`}
                    onSelect={() => handleSelect(item)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 data-[selected=true]:bg-slate-100 dark:data-[selected=true]:bg-slate-700/50"
                  >
                    {item.icon && <item.icon className="w-4 h-4 text-slate-400" />}
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{item.label}</p>
                      {item.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {item.description}
                        </p>
                      )}
                    </div>
                    {item.shortcut && (
                      <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-500 dark:text-slate-400">
                        {item.shortcut}
                      </kbd>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}

            {/* Hub Links */}
            {hubLinks.length > 0 && (
              <Command.Group heading="Navigate to Hub">
                {hubLinks.map((link) => (
                  <Command.Item
                    key={link.href}
                    value={`hub-${link.label}`}
                    onSelect={() => handleNavigate(link.href)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 data-[selected=true]:bg-slate-100 dark:data-[selected=true]:bg-slate-700/50"
                  >
                    <link.icon className="w-4 h-4 text-slate-400" />
                    <span className="flex-1 truncate">{link.label}</span>
                    <ExternalLink className="w-4 h-4 text-slate-400" />
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">↵</kbd>
                select
              </span>
            </div>
            <span>Quotech Command Palette</span>
          </div>
        </Command>
      </div>
    </>
  )
}

export default CommandPalette

